/**
 * Composable section INTERIORS (ADR 0054, plan §5/4a — the Designer Portal
 * repo's docs/PRICING_AND_INTERIORS_PLAN.md).
 *
 * A section's interior is an ordered list of ELEMENTS — drawer banks, rods,
 * shelves, shoe shelves, open space — composed by the designer the way Cabinet
 * Vision composes a section's front elevation. This module is the pure half:
 * it validates/normalizes the element list and SOLVES each element to a real
 * vertical span ({fromUnits, toUnits}, off the floor) inside the section's
 * carcass band. No DOM, no state — node-tested beside stacking.js.
 *
 * The element list reads TOP → BOTTOM, the way an elevation is read and the
 * way CV lists a unit's contents. The solver packs elements from the top of
 * the band downward:
 *   - FIXED-height elements consume exactly their height: a drawerBank is the
 *     sum of its drawer face heights (real CV banks: 7.42/8.66/8.68, 6.25×4,
 *     12.44 file drawers); a shelf is one shelf thickness.
 *   - FLEXIBLE elements (rod, shoeShelves, openSpace) share the segment's
 *     leftover equally. A rod's span is its HANG ZONE — the physical rod sits
 *     at the top of the zone (`rodAtUnits`), clothes hang down through it,
 *     which is why it is flexible: hang length is whatever the composition
 *     leaves it. Shoe shelves fill their span at `pitchUnits` = span / count.
 *   - A PINNED shelf (`atUnits` = the elevation of its TOP surface, off the
 *     floor — what a designer states with a tape) splits the band: elements
 *     before it in the list solve above it, elements after it below. A pin
 *     that cannot hold (outside the band, or above the previous pin) is
 *     IGNORED and flagged `pinIgnored`, never silently moved — the shelf then
 *     solves as an ordinary in-flow shelf.
 *   - OVERFLOW: when fixed heights exceed a segment, flexible elements go to
 *     zero, the overrunning elements are clamped at the segment bottom and
 *     flagged `overflow: true`, and the result carries the total
 *     `overflowUnits` with `ok: false` — a box the UI can badge, never a
 *     negative span.
 *
 * The BAND comes from the existing stacking pipeline (ADR 0040/0041/0050/0053
 * aware by construction): a unit placed by placeVerticalStack already carries
 * platform-, stack-, soffit- and to-height-aware {bottomUnits, topUnits}, and
 * unitShelves() gives its structural ¾" shelves — interiorBand() carves the
 * clear opening between them. Solving inside that band is what keeps this
 * module ignorant of platforms and ceilings, exactly like unitShelves.
 *
 * Element kinds v1 (ADR 0054). NOTE there is deliberately NO 'ESS' shoe-shelf
 * style: Elite toe stops are an ACCESSORY, not a shelf style (owner decision) —
 * they will attach through the per-element `accessories` array, a placeholder
 * every element carries for the approved accessories-on-elements concept
 * (locks, mirrors, toe stops…; empty in v1).
 */

import {
  rodHoleDropUnits,
  shelfHoleReference,
  shelfSpanAtHole,
  snapShelfRowToHoles,
  snapShelfToHole,
  systemHoleRows,
} from './system-holes.js';

/** The element kinds the interior model knows. Data guards check against it. */
export const INTERIOR_KINDS = Object.freeze(['drawerBank', 'rod', 'shelf', 'shelfGroup', 'shoeShelves', 'openSpace']);
const AUTO_HANG_ROLES = Object.freeze([
  'top-opening', 'top-divider', 'upper-rod', 'middle-divider', 'lower-rod',
]);

/**
 * A DOUBLE-THICK shelf is this many standard boards thick (owner, 2026-08-09:
 * "putting shelves vertically directly next to each other is how you create
 * double thick shelves in CV — it would be nicer if users could just select a
 * shelf and tag it as double thick"). Two ¾" boards = 1½". A rule
 * (`double_thick_shelf_multiplier`), not a constant, like everything numeric.
 */
export const DOUBLE_THICK_MULTIPLIER = 2;

/**
 * Default drawer-STACK assumptions, mirroring the SSH drawer tool's
 * `drawerCalcAssumptions` (Designer Portal `apps/portal/quick-tools.js`). Used
 * only when a caller passes none — the app always passes the business rules.
 */
export const DEFAULT_DRAWER_STACK = Object.freeze({
  toeHeightUnits: 2.5,
  overlayUnits: 0.375,
  verticalSpacingUnits: 0.125,
  maxHeightUnits: 48,
});

/** Shoe-shelf styles. NO 'ESS' — Elite toe stops are an accessory (ADR 0054). */
export const SHOE_SHELF_STYLES = Object.freeze(['FSS', 'SSS']);

/**
 * Door coverage options a section's `doors` field may state.
 *
 * `split` (ADR 0060 item 3, owner 2026-08-09) is an UPPER door band and a
 * LOWER door band on the SAME section, meeting at one stored elevation. It is
 * a fourth coverage rather than two door objects because the owner's ideal
 * gesture — dragging the edge between them so one grows as the other shrinks —
 * edits exactly ONE number; two independent heights could drift apart and
 * leave a gap or an overlap the shop cannot build.
 */
export const DOOR_COVERAGES = Object.freeze(['full', 'lower', 'upper', 'split']);

/**
 * Drawer FACE heights round to this (owner, 2026-08-09: "rounded to 1/4""). It
 * is a MODEL-entry rule, not a display one — see roundDrawerFaceUnits.
 */
export const DRAWER_FACE_ROUND_UNITS = 0.25;

const isPos = (v) => Number.isFinite(v) && v > 0;

/**
 * Rounds a drawer face height to the nearest quarter inch (owner, 2026-08-09).
 *
 * Deliberately applied where a value ENTERS the model from a designer's choice
 * (the editor's height select / +drawer / bank inserts are already standard),
 * NOT inside normalizeInteriorElement: the CV-derived `drawer_bank_presets`
 * carry real non-standard faces (7.42 / 8.66 / 8.68 / 12.44) and a stored bank
 * must round-trip untouched. Rounding at normalize would silently rewrite
 * every existing sketch on load — the opposite of what the owner asked for.
 */
export function roundDrawerFaceUnits(units) {
  if (!isPos(units)) return null;
  const q = DRAWER_FACE_ROUND_UNITS;
  return Math.max(q, Math.round(units / q) * q);
}

/** Accessories ride as an opaque-but-JSON array (placeholder, ADR 0054). */
function cleanAccessories(value) {
  if (!Array.isArray(value)) return [];
  return value.filter((a) => a && typeof a === 'object').map((a) => ({ ...a }));
}

/**
 * Normalizes ONE interior element to its full v1 shape, or null for junk.
 * Field-by-field, the way hydrateSketch rebuilds every other structure — an
 * unknown kind is dropped rather than guessed at, and every element gains its
 * `accessories` placeholder array.
 */
export function normalizeInteriorElement(el) {
  if (!el || typeof el !== 'object') return null;
  const accessories = cleanAccessories(el.accessories);
  // PERSISTENT IDENTITY (per-space doors foundation, 2026-08-29). Normalize
  // used to strip every unknown field, so an id died on the first
  // round-trip — and ADR 0065's "every space IS one element at one index"
  // left identity positional, which splits/heals/reorders silently move.
  // A string id rides through every case below; MINTING is deliberately not
  // done here (normalize stays deterministic and pure) — the model
  // boundaries call ensureInteriorElementIds.
  const identity = {
    ...(typeof el.id === 'string' && el.id ? { id: el.id } : {}),
    ...(AUTO_HANG_ROLES.includes(el.autoHangRole) ? { autoHangRole: el.autoHangRole } : {}),
  };
  switch (el.kind) {
    case 'drawerBank': {
      // TALLEST AT THE BOTTOM (owner, 2026-08-09): a bank's faces are always
      // arranged in ascending height from the floor. The list reads TOP →
      // BOTTOM, so ascending-from-the-floor is ascending order in the array's
      // REVERSE — i.e. shortest first (top), tallest last (floor end). Sorting
      // at NORMALIZE (not just on apply) is what makes it an invariant: a bank
      // from a preset, a stored sketch or a designer's edit all render the same
      // way, and the editor's Drw numbering can trust it.
      const drawers = (Array.isArray(el.drawers) ? el.drawers : [])
        .filter((d) => d && isPos(d.heightUnits))
        // `fixed: true` marks a face whose height is dictated by hardware —
        // a hamper's tilt-out unit (D113, owner 2026-08-27: "fixed drawer
        // heights"). Boolean-strict so junk data cannot freeze a face, and
        // carried per FACE so it travels with the face it describes through
        // the sort below rather than freezing whichever face lands at that
        // index. The editor refuses to resize a fixed face; the solver needs
        // nothing — a bank is fixed-height by construction either way.
        .map((d) => (d.fixed === true ? { heightUnits: d.heightUnits, fixed: true } : { heightUnits: d.heightUnits }))
        .sort((a, b) => a.heightUnits - b.heightUnits);
      // The bank's HANDLE TYPE (ADR 0064). Modelled as data on the ELEMENT
      // because the whole mounting rule keys off it — a tab pull is top-
      // mounted, everything else is centred — and because a bank is the unit a
      // designer chooses hardware for, not an individual face. A bare string
      // key into the `handle_types` rule; `null` means "the default type",
      // resolved at read time so changing the default rule moves every bank
      // that never stated one. Unvalidated here on purpose: this module knows
      // nothing about the rules, and handleType() already falls back safely on
      // a key the admin later deleted.
      const handleType = typeof el.handleType === 'string' && el.handleType ? el.handleType : null;
      return { ...identity, kind: 'drawerBank', drawers, handleType, accessories };
    }
    case 'rod':
      return {
        ...identity,
        kind: 'rod',
        // The hang CATEGORY attaches to the ROD ELEMENT, not the section type
        // (ADR 0054 — groundwork for the ADR 0051 step-2 re-keying).
        hangCategory: typeof el.hangCategory === 'string' && el.hangCategory ? el.hangCategory : null,
        accessories,
      };
    case 'shelf':
      return {
        ...identity,
        kind: 'shelf',
        fixed: el.fixed === true,
        // DOUBLE-THICK (ADR 0061 item C): ONE shelf of 2× the standard
        // thickness, not two shelves drawn beside each other. It is a TAG on
        // the shelf rather than a second element precisely because the owner
        // asked to stop expressing it as two adjacent boards.
        doubleThick: el.doubleThick === true,
        // LOCKED (owner, 2026-08-30: "a way to lock a shelf so it cannot be
        // inadvertently moved"). Exactly the fixture lock's meaning — it stops
        // the GESTURE, not the number: a locked shelf offers no drag target,
        // but its "Pin at" field still takes a typed height, because typing a
        // figure is deliberate and a drag is what happens by accident.
        //
        // A DIFFERENT AXIS FROM `fixed`, and the two must not be conflated:
        // `fixed` is how the board is ATTACHED (rafix bolts vs resting on
        // pins) and both kinds are movable; `locked` is whether the designer
        // may move it. An adjustable shelf can be locked, and a fixed shelf
        // can be left unlocked.
        locked: el.locked === true,
        ...(isPos(el.atUnits) ? { atUnits: el.atUnits } : {}),
        accessories,
      };
    case 'shelfGroup':
      // COUNT-BASED shelving (owner, 2026-08-09: "designer selects 5 shelves
      // and 5 evenly spaced shelves are inserted"). Deliberately the same
      // shape as shoeShelves — a flexible element carrying a COUNT that solves
      // to an even pitch — so there is ONE evenly-spaced-lines mechanism in
      // the solver, not two. A discrete `shelf` element stays the way to state
      // a shelf that is fixed, pinned, or structural (the item-4 rod support
      // shelf is one of those, and must never be swallowed into a group).
      return {
        ...identity,
        kind: 'shelfGroup',
        count: Number.isInteger(el.count) && el.count >= 0 ? el.count : 0,
        fixed: el.fixed === true,
        // A group's members share ONE tag (ADR 0061 item C): a group states
        // "N shelves like this", and a group whose members differed in
        // thickness would not be one element any more — that is a shelf each.
        doubleThick: el.doubleThick === true,
        accessories,
      };
    case 'shoeShelves':
      return {
        ...identity,
        kind: 'shoeShelves',
        // An unknown style falls to FSS (flat) rather than dropping the
        // designer's shelf count with it — the count is the real data.
        style: SHOE_SHELF_STYLES.includes(el.style) ? el.style : 'FSS',
        count: Number.isInteger(el.count) && el.count >= 0 ? el.count : 0,
        accessories,
      };
    case 'openSpace':
      return { ...identity, kind: 'openSpace', accessories };
    default:
      return null;
  }
}

/**
 * Normalizes a whole interior array. Returns:
 *   - null for a non-array (a LEGACY section that never composed one — the
 *     null is load-bearing: it is what tells readers to derive from the type
 *     preset + counts instead),
 *   - [] for an explicitly emptied interior,
 *   - otherwise the cleaned elements (junk entries dropped).
 */
/**
 * Gives every element of an interior a UNIQUE persistent id, minting only
 * where one is missing and re-minting duplicates (a copy-pasted element must
 * not share identity — identity is unique or it is not identity). Existing
 * ids are never changed, which is the property the whole per-space-doors
 * design leans on: a split/heal SPLICES new id-less elements between
 * survivors, and the survivors keep the identity a door may be attached to.
 * Null passes through — a null interior is the load-bearing legacy signal
 * (normalizeInterior's contract), not an empty list.
 */
export function ensureInteriorElementIds(interior, makeId) {
  const list = normalizeInterior(interior);
  if (!Array.isArray(list)) return list;
  const seen = new Set();
  return list.map((el) => {
    const id = typeof el.id === 'string' && el.id && !seen.has(el.id) ? el.id : String(makeId());
    seen.add(id);
    return { ...el, id };
  });
}

export function normalizeInterior(value) {
  if (!Array.isArray(value)) return null;
  return value.map(normalizeInteriorElement).filter(Boolean);
}

/**
 * Section-level doors are either the current covered-extent shape
 * `{fromElementId,toElementId,styleKey,handleType?}` or a legacy coverage
 * record. New writes always use element ids: those are the two spaces the
 * designer selected, and leaf count, vertical splits, area and price derive
 * later from the solved spans plus the admin-maintained style row.
 *
 * `heightUnits` (owner, 2026-08-09) is the DESIGNER-SET height of a `lower` or
 * `upper` door band; the other portion of the opening is the remainder. `null`
 * means "the legacy half" — the 50/50 split ADR 0055 shipped — so every sketch
 * stored before this change keeps drawing exactly what it drew.
 *
 * `splitAtUnits` is the one number a `split` pair meets at, an elevation off
 * the floor. `null` means the MIDPOINT of the door extent — the same
 * "unstated falls to the middle" convention, and what a fresh split starts at.
 *
 * Each field is dropped on the coverages it means nothing for (`full` derives
 * its whole extent; only `split` has a meeting edge), so no coverage can carry
 * a stale figure from a coverage the designer moved away from.
 */
export function normalizeDoors(value) {
  if (!value || typeof value !== 'object') return null;
  const fromElementId = typeof value.fromElementId === 'string' && value.fromElementId ? value.fromElementId : null;
  const toElementId = typeof value.toElementId === 'string' && value.toElementId ? value.toElementId : null;
  if (fromElementId && toElementId) {
    return {
      fromElementId,
      toElementId,
      styleKey: typeof value.styleKey === 'string' && value.styleKey ? value.styleKey : null,
      ...(typeof value.handleType === 'string' && value.handleType ? { handleType: value.handleType } : {}),
    };
  }
  // Legacy sketches remain drawable and removable, but this shape cannot
  // resolve the owner's new style rate and therefore prices needs-pricing.
  if (!DOOR_COVERAGES.includes(value.coverage)) return null;
  const banded = value.coverage === 'lower' || value.coverage === 'upper';
  return {
    coverage: value.coverage,
    heightUnits: banded && isPos(value.heightUnits) ? value.heightUnits : null,
    splitAtUnits: value.coverage === 'split' && isPos(value.splitAtUnits) ? value.splitAtUnits : null,
    ...(typeof value.handleType === 'string' && value.handleType ? { handleType: value.handleType } : {}),
  };
}

/**
 * The counts an interior implies — the DERIVED MIRRORS of the legacy
 * `shelfCount` / `drawerCount` fields (ADR 0054: the interior is the source
 * of truth; the counts are maintained from it so today's storage summary and
 * need rows keep reading what they always read).
 *
 *   - shelves: shelf elements + shelf-GROUP counts + shoe-shelf counts (a shoe
 *     shelf IS a shelf — the shoes storage math multiplies pairs-per-shelf by
 *     exactly this; a grouped shelf is just as real a board).
 *   - drawers: every drawer across every bank.
 *   - rods: rod elements (the hang math's `hangRods` equivalent).
 */
export function interiorCounts(interior) {
  const list = Array.isArray(interior) ? interior : [];
  let shelves = 0;
  let drawers = 0;
  let rods = 0;
  for (const el of list) {
    if (!el || typeof el !== 'object') continue;
    if (el.kind === 'shelf') shelves += 1;
    if (el.kind === 'shelfGroup' && Number.isInteger(el.count)) shelves += el.count;
    if (el.kind === 'shoeShelves' && Number.isInteger(el.count)) shelves += el.count;
    if (el.kind === 'drawerBank' && Array.isArray(el.drawers)) drawers += el.drawers.length;
    if (el.kind === 'rod') rods += 1;
  }
  return { shelves, drawers, rods };
}

/**
 * The clear interior BAND of a placed unit: the opening between its structural
 * shelves. Takes exactly what stacksForWall() already computes per unit — the
 * solved {bottomUnits, topUnits} and its unitShelves() result — so the band is
 * platform-, stack-, soffit- and to-height-aware for free.
 *
 * @param {{bottomUnits:number, topUnits:number}} placed
 * @param {{cap?:object|null, bottom?:object|null, top?:object|null}|null} shelves
 * @returns {{bottomUnits:number, topUnits:number}}
 */
export function interiorBand(placed, shelves) {
  const bottom = shelves?.bottom?.toUnits
    ?? shelves?.cap?.toUnits
    ?? placed.bottomUnits;
  const top = shelves?.top?.fromUnits ?? placed.topUnits;
  return { bottomUnits: bottom, topUnits: Math.max(bottom, top) };
}

/**
 * The DRAWER-STACK height formula, shared verbatim with the SSH drawer tool
 * (Designer Portal `apps/portal/quick-tools.js` → `drawerStackEstimatedHeight`,
 * owner 2026-08-09: "this math is also in the drawer tool"):
 *
 *   faces + toe + (shelfThickness − overlay) × 2 + verticalSpacing × count
 *
 * A drawer bank therefore occupies MORE than the sum of its faces: the base
 * trim / toe kick under the bottom face, a face-to-carcass reveal at each end,
 * and 1/8" of air per face. That is why a bank that "fit" on paper can overrun
 * a band — the sketch now says so instead of drawing a stack that cannot be
 * built. Every figure is a business rule (`drawer_toe_height_units`,
 * `drawer_overlay_units`, `drawer_vertical_spacing_units`), so the shop changes
 * the assumption in one place.
 *
 * @param {Array<{heightUnits:number}>} drawers
 * @param {object} [opts] toeHeightUnits / overlayUnits / verticalSpacingUnits /
 *   shelfThicknessUnits (the 0.75 board the reveal is measured against)
 * @returns {number} the stack's real occupied height
 */
export function drawerStackHeightUnits(drawers, opts = {}) {
  return stackHeight(drawers, drawerStackOpts(opts));
}

/** The formula against ALREADY-resolved assumptions (drawerStackOpts). */
function stackHeight(drawers, s) {
  const faces = (Array.isArray(drawers) ? drawers : []).filter((d) => isPos(d?.heightUnits));
  if (!faces.length) return 0;
  const faceSum = faces.reduce((sum, d) => sum + d.heightUnits, 0);
  return faceSum + s.toe + s.reveal * 2 + s.spacing * faces.length;
}

/**
 * The BAND a drawer bank consumes — which is NOT the SSH total (ADR 0063).
 *
 * The SSH figure above measures the whole thing the drawer tool models: FLOOR →
 * TOP OF THE SHELF ABOVE. It therefore contains, besides the faces, three
 * things this model already draws as real geometry somewhere else:
 *
 *   - the TOE / base trim, which is what holds the unit's BOTTOM DECK off the
 *     floor — it lives under the carcass, below the interior band entirely;
 *   - the two `(thickness − overlay)` reveals, i.e. the uncovered material of
 *     the two BOUNDING BOARDS, which since ADR 0062 are real `shelf` elements
 *     each consuming its own `section_shelf_thickness_units` of band.
 *
 * Charging the bank for them again is the double count the owner saw as "the
 * bottom drawer is not at the bottom shelf": the bank's box reserved 2.5" of
 * toe plus 1.5" of board it does not own, and the faces — laid from the top —
 * floated that far above the shelf under them.
 *
 * What a bank really occupies between its two boards is the FACE GEOMETRY:
 *
 *   faces + verticalSpacing × (count − 1) − overlay × 2
 *
 * because an overlay face laps HALF A BOARD onto the shelf at each end, so the
 * carcass opening between the boards is two overlays SHORTER than the faces
 * stack. There are `count − 1` gaps because the shop puts NO board between
 * drawers (owner, 2026-08-09: those "serve no purpose and cost money") — the
 * 1/8" `drawer_vertical_spacing_units` is the whole of what separates one face
 * from the next.
 *
 * Every figure is still the same business rule; nothing new was invented, and
 * `drawerStackHeightUnits` above is untouched — it is still what the editor
 * reports and what the 48" rule is written against.
 *
 * @param {Array<{heightUnits:number}>} drawers
 * @param {object} [opts] same shape as drawerStackHeightUnits
 * @returns {number} the clear opening the bank needs between its two boards
 */
export function drawerBankBandHeightUnits(drawers, opts = {}) {
  return bandHeight(drawers, drawerStackOpts(opts));
}

/** drawerBankBandHeightUnits against ALREADY-resolved assumptions. */
function bandHeight(drawers, s) {
  const faces = drawerFacesOf(drawers);
  if (!faces.length) return 0;
  return Math.max(0, faceExtent(faces, s.spacing) - s.overlay * 2);
}

/** The faces of a bank that are real (a zero-height face is not a drawer). */
function drawerFacesOf(drawers) {
  return (Array.isArray(drawers) ? drawers : []).filter((d) => isPos(d?.heightUnits));
}

/** Faces + the gaps BETWEEN them — the stack's own height, boards excluded. */
function faceExtent(faces, spacing) {
  const faceSum = faces.reduce((sum, d) => sum + d.heightUnits, 0);
  return faceSum + spacing * Math.max(0, faces.length - 1);
}

/** Resolved stack assumptions — one place the fallbacks live. */
function drawerStackOpts(opts = {}) {
  const shelfT = isPos(opts.shelfThicknessUnits) ? opts.shelfThicknessUnits : 0.75;
  const toe = Number.isFinite(opts.drawerToeHeightUnits) && opts.drawerToeHeightUnits >= 0
    ? opts.drawerToeHeightUnits : DEFAULT_DRAWER_STACK.toeHeightUnits;
  const overlay = Number.isFinite(opts.drawerOverlayUnits) && opts.drawerOverlayUnits >= 0
    ? opts.drawerOverlayUnits : DEFAULT_DRAWER_STACK.overlayUnits;
  const spacing = Number.isFinite(opts.drawerVerticalSpacingUnits) && opts.drawerVerticalSpacingUnits >= 0
    ? opts.drawerVerticalSpacingUnits : DEFAULT_DRAWER_STACK.verticalSpacingUnits;
  const maxHeight = isPos(opts.drawerStackMaxHeightUnits)
    ? opts.drawerStackMaxHeightUnits : DEFAULT_DRAWER_STACK.maxHeightUnits;
  return { toe, overlay, spacing, maxHeight, reveal: Math.max(0, shelfT - overlay) };
}

/**
 * Ensures every ROD carries its structural SUPPORT SHELF (owner, 2026-08-09:
 * "when there is a rod, unless it is just below the top shelf, there is
 * normally a fixed shelf directly ~2.5" above the rod. These shelves are
 * needed for structural integrity").
 *
 * Inserts a FIXED shelf immediately above any rod that has none. Two EXEMPT
 * cases, exactly as the owner stated them:
 *   - the rod is FIRST in the list, so it already hangs directly under the
 *     unit's structural top shelf — that board is the support;
 *   - the element directly above it is already a FIXED shelf — the solver then
 *     places the rod exactly `rod_support_shelf_gap_units` under it, so an
 *     existing support shelf becomes a real one rather than a second being
 *     stacked on top of it.
 * An ADJUSTABLE shelf above a rod is NOT a support: it is not structural, so a
 * fixed one is inserted above the rod anyway.
 *
 * This is the single helper the editor's add-element path AND the type/legacy
 * DERIVATION both call, so the two can never diverge (the whole point of
 * lifting it out of either).
 *
 * @param {Array<object>|null} interior
 * @returns {Array<object>} a NEW normalized list, top → bottom
 */
export function ensureRodSupportShelves(interior) {
  const list = normalizeInterior(interior) ?? [];
  const out = [];
  for (const el of list) {
    if (el.kind === 'rod' && out.length > 0) {
      const above = out[out.length - 1];
      if (!(above.kind === 'shelf' && above.fixed === true)) {
        out.push(normalizeInteriorElement({ kind: 'shelf', fixed: true }));
      }
    }
    out.push(el);
  }
  return out;
}

/** Whether the element above `index` is the rod's structural support shelf. */
function hasSupportShelfAbove(elements, index) {
  return isFixedShelf(elements[index - 1]);
}

/** A DISCRETE fixed shelf — the only thing that counts as structural support.
 *  A shelfGroup is deliberately excluded (ADR 0060 item 9: the support shelf
 *  must stay its own element, never swallowed into a count-based group). */
function isFixedShelf(el) {
  return !!el && el.kind === 'shelf' && el.fixed === true;
}

/**
 * Whether the shelf at `index` is a DRAWER BANK'S OWN BOARD — the one directly
 * above or directly below a bank (ADR 0062's sandwich). Adjacency, like
 * structuralShelfRoles(), so a board the designer placed there counts exactly
 * as much as one the invariant inserted.
 */
function isBankBoard(elements, index) {
  if (!isFixedShelf(elements[index])) return false;
  return elements[index + 1]?.kind === 'drawerBank' || elements[index - 1]?.kind === 'drawerBank';
}

/** A fresh structural shelf, normalized like any other element. */
function newFixedShelf() {
  return normalizeInteriorElement({ kind: 'shelf', fixed: true });
}

/**
 * Ensures every DRAWER BANK is SANDWICHED between FIXED shelves (owner,
 * 2026-08-09: "if there is a drawer/drawer bank, there needs to be a fixed
 * shelf directly below the bottom drawer (most often the bottom system shelf
 * unless a client wants doors or open shelving below the bottom drawer) and
 * directly above the top drawer — the shelves must be FIXED").
 *
 * Deliberately the twin of ensureRodSupportShelves(): the same idempotent
 * shape, the same "already there = leave it alone" exemptions, and the same
 * two call sites (the editor's structural edits AND the type/legacy
 * derivation), so the composed and the derived compositions cannot diverge.
 *
 * EXEMPTIONS — never duplicate structure that already exists:
 *   - the bank is FIRST in the top → bottom list, so the unit's own structural
 *     TOP/cap shelf already sits directly above it;
 *   - the bank is LAST, so the unit's structural BOTTOM shelf already sits
 *     directly below it — the owner's "most often the bottom system shelf";
 *   - the element directly adjacent on that side is ALREADY a discrete FIXED
 *     shelf (an ADJUSTABLE one is not structural, so a fixed one is inserted
 *     above/below it anyway).
 *
 * When there IS content below the bank — doors, open shelving, another
 * element, the owner's "unless a client wants doors or open shelving below the
 * bottom drawer" — the fixed shelf is still REQUIRED; it simply is not the
 * unit's bottom shelf. That case falls out of the list position: a bank with
 * anything after it is not last, so it gets its own board.
 *
 * @param {Array<object>|null} interior
 * @returns {Array<object>} a NEW normalized list, top → bottom
 */
export function ensureDrawerBankShelves(interior) {
  const list = normalizeInterior(interior) ?? [];
  const out = [];
  list.forEach((el, index) => {
    if (el.kind !== 'drawerBank') { out.push(el); return; }
    // ABOVE the top drawer. `out` is the list as it will really read, so a
    // shelf this pass just inserted counts — two stacked banks share one board.
    if (out.length > 0 && !isFixedShelf(out[out.length - 1])) out.push(newFixedShelf());
    out.push(el);
    // BELOW the bottom drawer.
    const below = list[index + 1];
    if (below && !isFixedShelf(below)) out.push(newFixedShelf());
  });
  return out;
}

/**
 * EVERY structural-shelf invariant in one idempotent call — the single helper
 * both the editor and the derivation run, so no path can carry a composition
 * the shop would not build (ADR 0060 item 4 + ADR 0062).
 *
 * Rods first, then banks: a bank whose neighbour is a rod then sees the rod's
 * own support shelf already in place and shares it rather than stacking a
 * second board against it.
 */
/**
 * Collapses runs of ADJACENT `openSpace` elements into one.
 *
 * ## The trap this closes
 *
 * Two empty spaces with nothing between them are physically ONE space — there
 * is no board at their shared edge, and the drawing shows none. But the model
 * held them as two elements, and every control that could rejoin them works on
 * a BOARD:
 *
 *   - Merge removes the divider between two spaces, so with no divider it is
 *     not offered in either direction (`mergeSpaceTargets`);
 *   - Split refuses a board on a space's own edge, because that leaves nothing
 *     on one side (`draftSplitSpace`) — and the shared edge IS each space's
 *     edge.
 *
 * So the boundary became unreachable: the designer could not rejoin the spaces
 * and could not put the board back either. Reported by the owner on
 * 2026-08-30, from deleting a shelf that sat between two empty spaces.
 *
 * ACCESSORIES ARE KEPT, from every space in the run and in order. A merge must
 * not silently discard something the designer attached — losing a valet rod
 * because two spaces became one would be a worse bug than the one this fixes.
 * The surviving element keeps the FIRST space's identity, matching
 * draftMergeSpace's rule that the space you act on is the one that survives.
 *
 * Deliberately NOT part of ensureInteriorStructure(): that pair only ever
 * INSERTS, and interiorStructureHealing()'s greedy alignment depends on it
 * (see that function's note). A helper that REMOVES would break the "a board
 * appeared" report. Structural edits call this alongside, not through it.
 *
 * @param {Array<object>|null} interior
 * @returns {Array<object>} a new list; runs of open space become single elements
 */
export function coalesceOpenSpaces(interior) {
  const list = normalizeInterior(interior) ?? [];
  const out = [];
  for (const el of list) {
    const prev = out[out.length - 1];
    if (el.kind === 'openSpace' && prev?.kind === 'openSpace') {
      // Fold into the survivor rather than pushing a second element.
      out[out.length - 1] = {
        ...prev,
        accessories: [...(prev.accessories ?? []), ...(el.accessories ?? [])],
      };
      continue;
    }
    out.push(el);
  }
  return out;
}

export function ensureInteriorStructure(interior) {
  return ensureDrawerBankShelves(ensureRodSupportShelves(interior));
}

/**
 * Two normalized elements that say the same thing. The normalizers emit a
 * FIXED key order per kind (see normalizeInteriorElement), so a serialization
 * compare is a real structural compare here and not a fragile one — and it is
 * the only place in this module that needs deep equality.
 */
function sameElement(a, b) {
  return JSON.stringify(a) === JSON.stringify(b);
}

/**
 * ensureInteriorStructure(), plus a REPORT of what it had to add (ADR 0065
 * part 1).
 *
 * The invariants exist because the shop cannot build a composition without
 * them, but a sketch stored BEFORE a given invariant shipped still holds the
 * old geometry: nothing rewrites a stored composition on load, deliberately
 * (ADR 0054 — "the stored doc only changes when the designer edits"), so a
 * section drawn in July never gained ADR 0062's drawer-bank sandwich or ADR
 * 0060's rod support shelf. The owner read that as the fix having failed.
 *
 * The answer is not to heal silently on load — that would rewrite every stored
 * job in the file the first time it is opened. It is to heal when the designer
 * OPENS the section for editing, into the staged draft, and to SAY SO. This
 * function is the "and to say so" half: the healing itself is still exactly
 * ensureInteriorStructure, so there is one implementation of the invariants
 * and this cannot drift from it.
 *
 * The alignment is a greedy two-pointer over `before` and the healed list. It
 * is exact rather than heuristic because the helpers only ever INSERT (never
 * remove, never reorder) and only ever insert the one shape newFixedShelf()
 * makes, directly beside the bank or rod that needed it — and they skip
 * insertion entirely when a conforming board is already there, which is what
 * removes the ambiguity a greedy match would otherwise have.
 *
 * @param {Array<object>|null} interior
 * @returns {{elements: Array<object>, added: Array<{index:number, role:string|null}>}}
 *   `added` carries each inserted board's index in the HEALED list and the
 *   structural role it plays there (structuralShelfRoles), so a caller can
 *   name what appeared without re-deriving why.
 */
export function interiorStructureHealing(interior) {
  const before = normalizeInterior(interior) ?? [];
  const elements = ensureInteriorStructure(before);
  const roles = structuralShelfRoles(elements);
  const added = [];
  let i = 0;
  elements.forEach((el, index) => {
    if (i < before.length && sameElement(before[i], el)) { i += 1; return; }
    added.push({ index, role: roles[index] });
  });
  return { elements, added };
}

/**
 * The STRUCTURAL ROLE each element plays, or null — what the editor's element
 * list labels so a designer can see why a board is there and that it is not
 * theirs to delete (ADR 0062 item 6).
 *
 * Derived from ADJACENCY, never from a stored flag: a fixed shelf directly
 * above a rod IS the rod's support whether the helper put it there or the
 * designer did, and a shelf that stops being adjacent stops being structural.
 * No new persisted field, so nothing to migrate and nothing to drift.
 *
 * @returns {Array<null|'rodSupport'|'drawerBankAbove'|'drawerBankBelow'>}
 */
export function structuralShelfRoles(elements) {
  const list = Array.isArray(elements) ? elements : [];
  return list.map((el, i) => {
    if (!isFixedShelf(el)) return null;
    if (list[i + 1]?.kind === 'rod') return 'rodSupport';
    if (list[i + 1]?.kind === 'drawerBank') return 'drawerBankAbove';
    if (list[i - 1]?.kind === 'drawerBank') return 'drawerBankBelow';
    return null;
  });
}

/**
 * ONE shelf's real thickness: the standard board, or `multiplier` boards when
 * the element is tagged DOUBLE-THICK (ADR 0061 item C). Every height, span, pin
 * check and hole snap reads this, so a double-thick shelf is thicker everywhere
 * at once rather than only where someone remembered.
 */
export function shelfThicknessUnits(el, shelfT = 0.75, multiplier = DOUBLE_THICK_MULTIPLIER) {
  const base = isPos(shelfT) ? shelfT : 0.75;
  const mult = isPos(multiplier) ? multiplier : DOUBLE_THICK_MULTIPLIER;
  return el?.doubleThick === true ? base * mult : base;
}

/** An element's fixed height, or null when it is flexible. */
function fixedHeight(el, shelfT, stack, mult) {
  if (Number.isFinite(el.autoHangHeightUnits) && el.autoHangHeightUnits >= 0) {
    return el.autoHangHeightUnits;
  }
  // A BANK CONSUMES THE FACE GEOMETRY, not the SSH total (ADR 0063): the toe
  // and the two bounding boards in that total are drawn elsewhere in this
  // model, so charging the band for them again floats the stack off its shelf.
  if (el.kind === 'drawerBank') return bandHeight(el.drawers, stack);
  if (el.kind === 'shelf') return shelfThicknessUnits(el, shelfT, mult);
  return null; // rod / shelfGroup / shoeShelves / openSpace flex
}

/**
 * Resolves the DEFAULT double-hang's optional top remainder without changing
 * ordinary designer-composed rods. The 84-inch clear band is the lower
 * assembly's reference; a taller band gets `top opening + divider` only when
 * the opening reaches the maintained minimum. Otherwise those two tagged
 * elements solve at zero and the two rod zones divide the whole band, leaving
 * no sliver remainder.
 */
function resolveAutoDoubleHang(elements, bandHeight, opts, shelfT, rodHoleDrop) {
  const roles = new Map(elements.map((el, index) => [el.autoHangRole, index]));
  if (!AUTO_HANG_ROLES.every((role) => roles.has(role))) return elements;
  const canonical = ['top-opening', 'top-divider', 'upper-rod', 'middle-divider', 'lower-rod'];
  if (elements.length !== canonical.length
    || canonical.some((role, index) => elements[index]?.autoHangRole !== role)) return elements;
  const referenceBand = opts.standardDoubleHangBandUnits;
  if (!(referenceBand > 0) || !(bandHeight > 0)) return elements;

  const supportGap = Math.max(0, rodHoleDrop - shelfT / 2);
  const referenceRod = Math.max(0, (referenceBand - shelfT - supportGap * 2) / 2);
  const topOpening = bandHeight - referenceBand - shelfT;
  const minimum = opts.autoTopOpeningMinUnits > 0 ? opts.autoTopOpeningMinUnits : 7;
  const includeTop = topOpening + EPS >= minimum;
  const rodHeight = includeTop
    ? referenceRod
    : Math.max(0, (bandHeight - shelfT - supportGap * 2) / 2);

  return elements.map((el) => {
    if (el.autoHangRole === 'top-opening') {
      return { ...el, autoHangHeightUnits: includeTop ? topOpening : 0, omitted: !includeTop };
    }
    if (el.autoHangRole === 'top-divider') {
      return { ...el, autoHangHeightUnits: includeTop ? shelfT : 0, omitted: !includeTop };
    }
    if (el.autoHangRole === 'upper-rod' || el.autoHangRole === 'lower-rod') {
      return { ...el, autoHangHeightUnits: rodHeight };
    }
    return el;
  });
}

const EPS = 1e-9;

/**
 * Solves an interior to real vertical spans inside a carcass band.
 *
 * @param {Array<object>} interior elements, TOP → BOTTOM (normalized or raw —
 *   raw input is normalized first, so junk can't produce NaN geometry)
 * @param {{bottomUnits:number, topUnits:number}} band the clear opening
 *   (interiorBand() of a placed unit)
 * @param {object} [opts]
 * @param {number} [opts.shelfThicknessUnits=0.75] a shelf element's thickness
 *   (the section_shelf_thickness_units rule — passed in, never read here)
 * @param {number} [opts.rodSupportShelfHoleOffset=2] how many SYSTEM HOLES
 *   separate a rod's top from the hole its covering fixed shelf is centred on
 *   (ADR 0062 §7, superseding ADR 0060 item 4's free 2.5" gap). Consumed as a
 *   fixed height, so the rod line lands exactly that many pitches under the
 *   hole — on a hole itself, with one visible hole between the two.
 * @param {number} [opts.drawerToeHeightUnits] SSH drawer-tool assumptions —
 * @param {number} [opts.drawerOverlayUnits]     see drawerStackHeightUnits.
 * @param {number} [opts.drawerVerticalSpacingUnits]
 * @param {number} [opts.drawerStackMaxHeightUnits=48] a bank taller than this
 *   is flagged `stackOverMax` (the drawer tool's 48" warning).
 * @param {number} [opts.doubleThickMultiplier=2] how many standard boards a
 *   DOUBLE-THICK shelf is (ADR 0061 item C).
 * @param {number} [opts.systemHolePitchMm=32] the system pitch, and
 * @param {number} [opts.systemHoleFirstOffsetMm=9.5] the first hole's centre
 *   above the PANEL'S BOTTOM EDGE (ADR 0061 item A) — shelves snap to this
 *   ladder. Both are millimetres: the shop's system is metric.
 * @param {number} [opts.panelBottomUnits] the placed unit's own bottom edge,
 *   off the floor — the DATUM the hole ladder is measured from. Omit only when
 *   there is genuinely no panel (a bare band); it then falls back to the band
 *   bottom, which is a couple of inches higher and shifts every hole.
 * @param {boolean} [opts.requiredHolesOnly=false] the SECTION's per-section
 *   flag (ADR 0061 item B): the shop drills only the REQUIRED holes (drawer
 *   banks + the top/bottom shelves), so shelves take exact designer positions
 *   and are NOT snapped to the 32 mm grid.
 * @returns {{
 *   elements: Array<object>,  // same order; each element + fromUnits/toUnits
 *                             // (+ overflow/pinIgnored flags, and per-kind
 *                             //  extras: solved drawers, rodAtUnits, pitchUnits,
 *                             //  shelfLines, thicknessUnits, holeUnits)
 *   overflowUnits: number,    // how far the fixed content overruns the band
 *   ok: boolean,              // false when anything overflowed
 *   holes: object,            // the drilling model — see holeModel()
 *   emptySegments: Array<{fromUnits, toUnits, heightUnits, insertIndex}>,
 *                             // regions the dividers cut that hold NO element
 *                             // (ADR 0067) — real space the drawing shows and
 *                             // an element list cannot name
 * }}
 */
export function solveInterior(interior, band = {}, opts = {}) {
  const shelfT = isPos(opts.shelfThicknessUnits) ? opts.shelfThicknessUnits : 0.75;
  const mult = isPos(opts.doubleThickMultiplier) ? opts.doubleThickMultiplier : DOUBLE_THICK_MULTIPLIER;
  const stack = drawerStackOpts({ ...opts, shelfThicknessUnits: shelfT });
  // THE ROD'S DROP IS A HOLE COUNT (ADR 0062 §7, owner 2026-08-09): the fixed
  // shelf covers a hole and the rod's top sits `rodSupportShelfHoleOffset`
  // pitches below THAT HOLE, leaving exactly one hole visible between them. The
  // drop is derived from the pitch rule, never typed — at 32 mm it is 64 mm =
  // 2.5196850", which is what the owner's earlier "~2.5 inches" always was.
  const rodHoleDrop = rodHoleDropUnits(opts.rodSupportShelfHoleOffset, { pitchMm: opts.systemHolePitchMm });
  const requiredOnly = opts.requiredHolesOnly === true;
  let normalized = normalizeInterior(interior) ?? [];
  /** The dead space a rod gives up to its support shelf — 0 when it has none. */
  const bandBottom = Number.isFinite(band.bottomUnits) ? band.bottomUnits : 0;
  const bandTop = Number.isFinite(band.topUnits) ? Math.max(bandBottom, band.topUnits) : bandBottom;
  const bandFor = { bottomUnits: bandBottom, topUnits: bandTop };
  const thickOf = (el) => shelfThicknessUnits(el, shelfT, mult);
  normalized = resolveAutoDoubleHang(normalized, bandTop - bandBottom, opts, shelfT, rodHoleDrop);

  // ---- the 32 mm hole ladder (ADR 0061 item A) ----------------------------
  // Built BEFORE anything solves, because a pinned shelf's datum has to be a
  // LEGAL position before the segments are cut at it — snapping afterwards
  // would move a datum every other element had already been solved against.
  // Shelf thickness never changes the ladder. Adjustable shelves put their
  // BOTTOM on a row and every fixed shelf puts its CENTRE on a row; the one
  // conversion in shelfSpanAtHole supplies both placements.
  const rows = systemHoleRows(bandFor, {
    pitchMm: opts.systemHolePitchMm,
    firstOffsetMm: opts.systemHoleFirstOffsetMm,
    panelBottomUnits: opts.panelBottomUnits,
  });
  const ctx = { shelfT, mult, stack, rodHoleDrop, bandBottom, bandTop, band: bandFor, rows, snap: !requiredOnly, thickOf };

  // A USER PIN states a height the designer measured; the shop can still only
  // drill on a hole, so the pin lands on the nearest legal one and says where
  // it came from (`pinSnappedFromUnits`) rather than pretending the tape was
  // law. In "required holes only" mode the tape IS law — no ladder, no snap.
  const seeded = requiredOnly ? normalized : normalized.map((el) => {
    if (el.kind !== 'shelf' || !isPos(el.atUnits)) return el;
    const t = thickOf(el);
    // A pin the band cannot hold at all is left EXACTLY where the designer put
    // it: snapping it would move it silently into the band, and the whole
    // point of `pinIgnored` is that an impossible pin is reported, not fixed.
    if (el.atUnits - t < bandBottom - EPS || el.atUnits > bandTop + EPS) return el;
    const snapped = snapShelfToHole({ fromUnits: el.atUnits - t, toUnits: el.atUnits }, rows, {
      fixed: el.fixed === true, thicknessUnits: t, band: bandFor,
    });
    if (!snapped || Math.abs(snapped.toUnits - el.atUnits) < 1e-9) return el;
    return { ...el, atUnits: snapped.toUnits, pinSnappedFromUnits: el.atUnits, holeUnits: snapped.holeUnits };
  });

  // ---- solve, then RE-solve against the holes the shelves landed on -------
  // Snapping cannot be a cosmetic pass over a finished layout: moving a board
  // moves the clear space above and below it, and everything under a shelf is
  // measured from that shelf. So the first pass finds each in-flow shelf's
  // even-distribution INTENT, the snap turns that intent into a legal hole,
  // and the second pass re-solves with those holes as DATUMS — reusing the
  // pin machinery that already knows how to solve around a fixed height,
  // rather than growing a second one (the ADR 0060 item 9 discipline).
  let laid = layoutInterior(seeded, ctx);
  if (!requiredOnly) {
    const repinned = deriveShelfPins(seeded, laid.out, ctx);
    if (repinned) laid = layoutInterior(repinned, ctx);
  }
  const out = laid.out.map(stripDerivedPin);
  return {
    elements: out,
    overflowUnits: laid.overflowUnits,
    ok: laid.overflowUnits <= EPS,
    holes: holeModel(out, rows, { requiredOnly, band: bandFor, shelfThicknessUnits: shelfT }),
    // The regions the DIVIDERS cut that hold no element at all (ADR 0067).
    emptySegments: laid.emptySegments,
  };
}

/**
 * Turns each in-flow shelf's SOLVED position into a derived pin at the nearest
 * legal system hole. Returns a new element list, or null when nothing moved.
 *
 * The row is snapped as a ROW (snapShelfRowToHoles) so two shelves the layout
 * put close together cannot collapse onto one hole and draw one board where the
 * designer composed two.
 */
function deriveShelfPins(elements, solved, ctx) {
  const targets = [];
  elements.forEach((el, index) => {
    // The C3 default-DH dividers are part of one derived 84-inch assembly.
    // Re-snapping either board independently would manufacture a remainder
    // after the subtraction has already closed, so they keep that assembly's
    // derived datum. C2 owns the global fixed/adjustable hole-centre rule.
    if (el.kind !== 'shelf' || el.autoHangRole || el.omitted === true || isPos(el.atUnits)) return; // user pins already legal
    // A BANK'S OWN BOARD IS NOT ON THE LADDER (ADR 0063). Snapping it up to the
    // nearest 32 mm row would open a gap between the board and the drawer face
    // that overlays it, and the only ways to close that gap again — stretching
    // the faces or widening the 1/8" between them — are both things the owner
    // says the shop does not do. The board follows the BANK; holeModel still
    // applies C2's fixed-shelf centre datum and records that required bore.
    if (isBankBoard(elements, index)) return;
    const s = solved[index];
    if (!s || s.overflow === true) return; // a board that does not fit stays where the clamp put it
    targets.push({ index, el, span: { fromUnits: s.fromUnits, toUnits: s.toUnits } });
  });
  if (!targets.length) return null;
  // The list reads top → bottom, so ascending elevation is the REVERSE.
  const ascending = targets.slice().reverse();
  const refs = ascending.map(({ el, span }) => shelfHoleReference(span, { fixed: el.fixed === true }));
  // A shelf whose NEXT element (the one below it) has a FIXED height — a drawer
  // bank, another board — may only round UP (ADR 0062). Rounding down would
  // take room from something that cannot shrink, so the stock DRW composition
  // would report an overflow no designer could clear. The intent is unchanged;
  // only which side of it the landing is allowed on.
  const minima = ascending.map(({ index }) => (
    fixedHeight(elements[index + 1] ?? {}, ctx.shelfT, ctx.stack, ctx.mult) === null
      ? null : refs[ascending.findIndex((t) => t.index === index)]
  ));
  const holes = snapShelfRowToHoles(refs, ctx.rows, { minima });
  const pinFor = new Map();
  ascending.forEach(({ index, el }, k) => {
    const hole = holes[k];
    if (!Number.isFinite(hole)) return;
    const t = ctx.thickOf(el);
    const span = shelfSpanAtHole(hole, { fixed: el.fixed === true, thicknessUnits: t });
    if (span.fromUnits < ctx.bandBottom - EPS || span.toUnits > ctx.bandTop + EPS) return;
    pinFor.set(index, { atUnits: span.toUnits, holeUnits: hole });
  });
  if (!pinFor.size) return null;
  return elements.map((el, index) => {
    const pin = pinFor.get(index);
    return pin ? { ...el, atUnits: pin.atUnits, holeUnits: pin.holeUnits, derivedPin: true } : el;
  });
}

/** A derived pin is an INTERNAL datum, never a pin the designer set: it comes
 *  back off the solved element so nothing downstream mistakes a snapped shelf
 *  for one the designer nailed down. */
function stripDerivedPin(solved) {
  if (!solved?.derivedPin) return solved;
  const { derivedPin, atUnits, ...rest } = solved;
  void derivedPin; void atUnits;
  return rest;
}

/**
 * The seat of the topmost element of the LAST drawer bank's rigid group in a
 * segment — the bank itself, or the fixed board directly above it, which is
 * the bank's own and moves with it (ADR 0063). Returns -1 when the segment
 * holds no bank, in which case there is nothing whose seating slack could
 * spoil.
 */
function bankGroupTopSeat(items) {
  const kinds = items.map(({ el }) => el.kind);
  const bank = kinds.lastIndexOf('drawerBank');
  if (bank < 0) return -1;
  return bank > 0 && isFixedShelf(items[bank - 1].el) ? bank - 1 : bank;
}

/**
 * ONE pass of the segment solver: cut the list at the pins that hold, then
 * pack each segment top-down. Called twice by solveInterior (see there).
 *
 * @returns {{out: Array<object>, overflowUnits: number}}
 */
function layoutInterior(elements, ctx) {
  const { shelfT, mult, stack, rodHoleDrop, bandBottom, bandTop, thickOf } = ctx;
  /**
   * The dead space a supported ROD gives up above it (ADR 0062 §7).
   *
   * The datum is the covering shelf's HOLE — a fixed shelf is CENTRED on its
   * hole (ADR 0061) — and the rod's top sits `rodHoleDrop` (two pitches) below
   * it. The cursor arrives at the shelf's UNDERSIDE, half a board below that
   * hole, so the clear space between the two is the drop LESS that half board.
   * Stated as the relationship, so a thicker (or double-thick) support board
   * moves the rod by exactly the right amount.
   *
   * A rod FIRST in the list hangs under the unit's own structural top shelf —
   * ADR 0060 item 4's exemption from an EXTRA board — but it obeys the same
   * hole relationship to that board, which is the owner's "rods below the top
   * shelf … between the 3rd and 4th system hole from the top of the panel".
   */
  const supportGap = (el, index) => {
    if (el.kind !== 'rod') return 0;
    if (hasSupportShelfAbove(elements, index)) {
      return Math.max(0, rodHoleDrop - thickOf(elements[index - 1]) / 2);
    }
    if (index === 0) return Math.max(0, rodHoleDrop - shelfT / 2);
    return 0;
  };

  // ---- split at valid pins ------------------------------------------------
  // A pinned shelf is a hard horizontal datum: everything before it in the
  // list solves between the previous datum and the pin, everything after
  // between the pin and the next one. Pins must descend (the list is
  // top→bottom); one that cannot hold is flagged and rejoins the flow.
  const segments = []; // { items: [{el, index}], pin: {el, index}|null }
  let current = { items: [], pin: null };
  let prevDatum = bandTop; // the top of the CURRENT segment
  const pinned = new Map(); // index -> true when honoured
  elements.forEach((el, index) => {
    if (el.kind === 'shelf' && isPos(el.atUnits)) {
      const holds = el.atUnits <= prevDatum + EPS && el.atUnits - thickOf(el) >= bandBottom - EPS;
      if (holds) {
        pinned.set(index, true);
        segments.push({ ...current, pin: { el, index } });
        current = { items: [], pin: null };
        prevDatum = el.atUnits - thickOf(el);
        return;
      }
      // Pin can't hold here — never silently move it; solve in flow, flagged.
    }
    current.items.push({ el, index });
  });
  segments.push(current);

  // ---- solve each segment top-down ---------------------------------------
  const out = new Array(elements.length);
  let overflowUnits = 0;
  let segTop = bandTop;
  /**
   * SEGMENTS THAT HOLD NO ELEMENT (ADR 0067). A pinned board cuts the band in
   * two, and nothing says the designer put anything on both sides of it: a
   * composition of `[shelf pinned at 60", rod, openSpace]` leaves everything
   * ABOVE 60" with no element in it at all. The drawing shows that region
   * because the region is real — it is the space between the board and the
   * carcass top — but a list of ELEMENTS cannot name it, which is exactly the
   * gap the owner read as "the numbers don't make sense compared to the
   * pictures". The solver is the only thing that knows a segment came out
   * empty, so the solver is what reports it; the editor then materialises an
   * `openSpace` there, and the reading of an interior as SPACES (ADR 0065) is
   * whole again.
   *
   * `insertIndex` is the position in the ELEMENT LIST an element belongs at to
   * land in this segment — before the pin that closes it, or at the end for
   * the last segment.
   */
  const emptySegments = [];
  for (const seg of segments) {
    const segBottom = seg.pin ? seg.pin.el.atUnits : bandBottom;
    const segHeight = Math.max(0, segTop - segBottom);
    if (seg.items.length === 0 && segHeight > EPS) {
      emptySegments.push({
        fromUnits: segBottom,
        toUnits: segTop,
        heightUnits: segHeight,
        insertIndex: seg.pin ? seg.pin.index : elements.length,
      });
    }
    // A rod's support gap is FIXED content (it is real, unusable space), so it
    // comes out of the segment before the flexible elements share the rest.
    const fixedSum = seg.items.reduce(
      (sum, { el, index }) => sum + (fixedHeight(el, shelfT, stack, mult) ?? 0) + supportGap(el, index), 0,
    );
    const flexCount = seg.items.filter(({ el }) => fixedHeight(el, shelfT, stack, mult) === null).length;
    const free = Math.max(0, segHeight - fixedSum);
    const flexH = flexCount > 0 ? free / flexCount : 0;
    if (fixedSum > segHeight + EPS) overflowUnits += fixedSum - segHeight;
    // SLACK GOES ABOVE A DRAWER BANK, NEVER INSIDE OR UNDER IT (ADR 0063,
    // superseding ADR 0062's "the bank takes up the slack"). With no flexible
    // element in the segment the leftover has to land somewhere, and the
    // original pooling at the segment BOTTOM drew the bank hanging clear of its
    // deck. ADR 0062 answered that by stretching the bank's box, which is what
    // the owner then saw as the bottom drawer floating above its shelf: a
    // stretched box cannot change the stack (the faces are standard heights and
    // the 1/8" between them is a rule), so the stretch became a gap.
    //
    // A bank and the board directly above it are RIGID as a pair, so the slack
    // is given up ABOVE that pair. The bank keeps its true height, stands on
    // what is under it, and its board stays hard against it.
    const slackSeat = flexCount === 0 && free > EPS ? bankGroupTopSeat(seg.items) : -1;

    let cursor = segTop;
    for (const [seat, { el, index }] of seg.items.entries()) {
      const fixed = fixedHeight(el, shelfT, stack, mult);
      const height = fixed === null ? flexH : fixed;
      // The gap sits ABOVE the element: a supported rod's zone starts exactly
      // `rodGap` below the shelf's underside, which is where the shelf above
      // left the cursor. Everything else has gap 0 and is unchanged.
      const gap = supportGap(el, index) + (seat === slackSeat ? free : 0);
      const elTop = cursor - gap;
      const rawFrom = elTop - height;
      const fromUnits = Math.max(rawFrom, segBottom);
      const toUnits = Math.max(Math.min(elTop, segTop), fromUnits);
      const solved = { ...el, fromUnits, toUnits };
      const rodGap = supportGap(el, index);
      if (rodGap > 0) solved.supportGapUnits = rodGap;
      if (seat === slackSeat) solved.slackAboveUnits = free;
      if (rawFrom < segBottom - EPS) solved.overflow = true;
      // A DERIVED pin (a snapped shelf) that could not hold is not a designer's
      // mistake, so it never raises the pinIgnored warning — it simply solves
      // in flow, exactly as it would have without the ladder.
      if (el.kind === 'shelf' && isPos(el.atUnits) && !pinned.get(index) && !el.derivedPin) solved.pinIgnored = true;
      decorate(solved, stack, ctx);
      out[index] = solved;
      cursor = rawFrom; // the NOMINAL bottom, so a following element's overrun is visible too
    }

    if (seg.pin) {
      const { el, index } = seg.pin;
      const t = thickOf(el);
      const solved = { ...el, fromUnits: el.atUnits - t, toUnits: el.atUnits };
      decorate(solved, stack, ctx);
      out[index] = solved;
      segTop = el.atUnits - t;
    }
  }

  return { out, overflowUnits, emptySegments };
}

/**
 * The DRILLING model — what the shop actually bores in this section.
 *
 * It is part of the solved result rather than prose (ADR 0061 item B) so the
 * editor, the elevation and the tests all read the same answer:
 *
 *   - `mode: 'full'`     — the whole 32 mm ladder is drilled; `elevations` is
 *     it, and `numbers` carries the owner's hole number n for each row.
 *   - `mode: 'required'` — "required holes only": the ladder is NOT drilled and
 *     `elevations` is empty. `required` then lists the holes that are still
 *     bored and WHY — each composed shelf, drawer bank and structural deck —
 *     which is what the word "required" means.
 *
 * @returns {{mode:'full'|'required', pitchUnits:number, originUnits:number,
 *   elevations:Array<number>,
 *   required:Array<{atUnits:number, reason:string}>}}
 */
function holeModel(solvedElements, rows, { requiredOnly = false, band = {}, shelfThicknessUnits = 0.75 } = {}) {
  const list = Array.isArray(solvedElements) ? solvedElements.filter(Boolean) : [];
  const required = [];
  const add = (atUnits, reason) => {
    if (!Number.isFinite(atUnits)) return;
    if (required.some((r) => Math.abs(r.atUnits - atUnits) < 1e-6)) return;
    required.push({ atUnits, reason });
  };
  // The unit's own top and bottom decks are fixed shelves too. `band` names
  // their inner FACES, so convert each face to the board CENTRE here, through
  // the same fixed-shelf datum relationship used for every interior shelf.
  const t = isPos(shelfThicknessUnits) ? shelfThicknessUnits : 0.75;
  add(shelfHoleReference({ fromUnits: band.bottomUnits - t, toUnits: band.bottomUnits }, { fixed: true }), 'bottomShelf');
  add(shelfHoleReference({ fromUnits: band.topUnits, toUnits: band.topUnits + t }, { fixed: true }), 'topShelf');
  for (const [index, el] of list.entries()) {
    // ONE datum conversion for every horizontal shelf surface. Adjustable
    // shelves bore at their bottom; fixed shelves — including the boards
    // immediately above/below drawers — bore at their centre. Thickness only
    // changes the span supplied to shelfHoleReference; it never moves the row.
    // The drawer-board `fixed: true` guarantee is established by
    // ensureInteriorStructure() on every derived composition and again at the
    // editor's Apply boundary; isBankBoard() deliberately labels only a board
    // that satisfies that structural guarantee.
    if (el.kind === 'shelf') {
      add(shelfHoleReference(el, { fixed: el.fixed === true }),
        isBankBoard(list, index) ? 'drawerShelf' : (el.fixed === true ? 'fixedShelf' : 'adjustableShelf'));
    }
    if (el.kind === 'shelfGroup') {
      const thickness = isPos(el.thicknessUnits) ? el.thicknessUnits : t;
      for (const top of el.shelfLines ?? []) {
        add(shelfHoleReference({ fromUnits: top - thickness, toUnits: top }, { fixed: el.fixed === true }),
          el.fixed === true ? 'fixedShelf' : 'adjustableShelf');
      }
    }
    // A drawer bank's slides are screwed to a drilled row whatever the mode.
    if (el.kind === 'drawerBank' && el.drawers?.length > 0) {
      add(el.toUnits, 'drawerBank');
      add(el.fromUnits, 'drawerBank');
    }
  }
  required.sort((a, b) => a.atUnits - b.atUnits);
  return {
    mode: requiredOnly ? 'required' : 'full',
    pitchMm: rows.pitchMm,
    firstOffsetMm: rows.firstOffsetMm,
    panelBottomUnits: rows.panelBottomUnits,
    numbers: requiredOnly ? [] : rows.numbers.slice(),
    elevations: requiredOnly ? [] : rows.elevations.slice(),
    required,
  };
}

/**
 * The OPENINGS a designer can select and split (ADR 0061 item D — how CV
 * works): the clear vertical spaces in a composition, which in this model are
 * exactly the solved `openSpace` elements. Carries the element index, so
 * inserting into an opening is a real edit to a real element rather than a
 * position the editor has to re-derive.
 *
 * @returns {Array<{elementIndex:number, fromUnits:number, toUnits:number, spanUnits:number}>}
 */
export function interiorOpenings(solvedElements) {
  const out = [];
  (Array.isArray(solvedElements) ? solvedElements : []).forEach((el, elementIndex) => {
    if (el?.kind !== 'openSpace') return;
    const spanUnits = el.toUnits - el.fromUnits;
    if (!(spanUnits > EPS)) return;
    out.push({ elementIndex, fromUnits: el.fromUnits, toUnits: el.toUnits, spanUnits });
  });
  return out;
}

/**
 * The kinds that are SPACES rather than dividers (ADR 0065 part 2).
 *
 * The owner reads an interior as a stack of vertical SPACES, not as a flat list
 * of parts: shelves "are generally evenly spaced within a vertical space… there
 * may be a vertical space above a drawer bank that is 24" tall to serve as a
 * vanity… if we can split openings into different spaces then count control
 * within the space is a strong first start."
 *
 * So the model names the two roles an element can play:
 *
 *   - a SPACE occupies vertical room and carries a TREATMENT — nothing
 *     (`openSpace`), N evenly spaced shelves (`shelfGroup`), N shoe shelves
 *     (`shoeShelves`), a hang zone (`rod`), or a bank of drawers (`drawerBank`);
 *   - a DIVIDER is a board between spaces — the discrete `shelf` element,
 *     which is also what a SPLIT inserts.
 *
 * This is a READING of the existing element list, not a new stored structure:
 * every space IS one element at one index, so a space edit is an ordinary edit
 * to an ordinary element and nothing needs migrating. That is the whole reason
 * the abstraction can be introduced without touching storage or sync.
 */
export const SPACE_KINDS = Object.freeze(['openSpace', 'shelfGroup', 'shoeShelves', 'rod', 'drawerBank']);

/** Whether an element is a SPACE (see SPACE_KINDS) rather than a divider. */
export function isSpaceKind(kind) {
  return SPACE_KINDS.includes(kind);
}

/**
 * The TREATMENT a space carries — the thing the designer chose to put in it,
 * as a small descriptor the editor's fill control round-trips through.
 *
 * `key` is the treatment, not the element kind, because "empty" and "shelves"
 * are choices a designer makes about ONE space; that they happen to be two
 * element kinds is an implementation fact the fill UI should not have to know.
 *
 * @returns {{key:'empty'|'shelves'|'shoes'|'rod'|'drawers', count:number,
 *   fixed?:boolean, style?:string}|null}
 */
export function spaceTreatment(el) {
  switch (el?.kind) {
    case 'openSpace': return { key: 'empty', count: 0 };
    case 'shelfGroup': return { key: 'shelves', count: el.count ?? 0, fixed: el.fixed === true };
    case 'shoeShelves': return { key: 'shoes', count: el.count ?? 0, style: el.style ?? 'FSS' };
    case 'rod': return { key: 'rod', count: 1 };
    case 'drawerBank': return { key: 'drawers', count: el.drawers?.length ?? 0 };
    default: return null;
  }
}

/**
 * The SPACES of a SOLVED interior, top → bottom — what the editor lists as the
 * primary abstraction (ADR 0065 part 2).
 *
 * Deliberately the generalisation of interiorOpenings() rather than a rival to
 * it: an opening is a space whose treatment is `empty`, so interiorOpenings()
 * stays exactly what ADR 0061 item D defined (the shelf-insert target) and this
 * is the wider reading the space editor works against. Both carry the ELEMENT
 * INDEX for the same reason — an edit to a space is an edit to a real element.
 *
 * @returns {Array<{elementIndex:number, elementId:string|null, kind:string, fromUnits:number,
 *   toUnits:number, heightUnits:number, treatment:object}>}
 */
export function interiorSpaces(solvedElements, opts = {}) {
  const els = Array.isArray(solvedElements) ? solvedElements : [];
  const out = [];
  els.forEach((el, elementIndex) => {
    if (!el || el.omitted === true || !isSpaceKind(el.kind)) return;
    const heightUnits = Math.max(0, (el.toUnits ?? 0) - (el.fromUnits ?? 0));
    out.push({
      elementIndex,
      elementId: typeof el.id === 'string' && el.id ? el.id : null,
      kind: el.kind,
      fromUnits: el.fromUnits,
      toUnits: el.toUnits,
      heightUnits,
      hangLengthUnits: el.kind === 'rod' ? rodHangLengthUnits(els, elementIndex, opts) : null,
      treatment: spaceTreatment(el),
    });
  });
  return out;
}

/**
 * HANG LENGTH — the figure Cabinet Vision does not give you (owner,
 * 2026-08-11): *"You often need to know the hang length of a section, which is
 * the top of the rod to the shelf below… because clients want to know that
 * their clothes won't drag on the shelves below."*
 *
 * ⚠ **This is NOT the opening.** The opening is the clear distance between two
 * boards; the rod hangs `rod_support_shelf_hole_offset` holes below the board
 * above it (ADR 0062 §7), so at the stock 2-hole drop:
 *
 *     opening = hang length + 2.1447"
 *
 * A solved rod's own span already runs from the boundary BELOW it to the top of
 * the rod, so in the ordinary case the hang length IS that span — which is why
 * the SPACES list has been quietly reporting hang lengths while the drawing
 * showed openings, and why the two never added up to the section (ADR 0067's
 * "still open" note). Same numbers, two different measurements.
 *
 * The owner's two EXCEPTIONS, both measured to where a garment would foul:
 *   1. **No board below at all** (the section's bottom shelf is switched off,
 *      ADR 0070): measure to `rod_hang_floor_clearance_units` (1") OFF THE
 *      FLOOR — an ABSOLUTE elevation, not a band offset.
 *   2. **A rod directly below with no board between** (the owner's "exception
 *      that is needed sometimes"): measure to 1" above THAT ROD, the same
 *      clearance, because the rod below is what the clothes would foul.
 *
 * @param {Array<object>} els solved elements, top → bottom
 * @param {number} index the rod's index in `els`
 * @param {{hasBottomShelf?:boolean, rodHangFloorClearanceUnits?:number}} [opts]
 * @returns {number} the hang length in units, never negative
 */
export function rodHangLengthUnits(els, index, opts = {}) {
  const rod = els?.[index];
  if (!rod || rod.kind !== 'rod') return 0;
  const clearance = isPos(opts.rodHangFloorClearanceUnits) ? opts.rodHangFloorClearanceUnits : 1;
  const top = rod.toUnits ?? 0;
  // What sits directly below, ignoring nothing — the first element that is a
  // real boundary. A board is the normal case and is already where the rod's
  // own span stops, so `rod.fromUnits` covers it without a search.
  const below = els.slice(index + 1).find((e) => e && (e.kind === 'shelf' || e.kind === 'rod'));
  let datum = rod.fromUnits ?? 0;
  if (below?.kind === 'rod') {
    // Exception 2 — no board between two rods. Taken DIRECTLY, not clamped to
    // the rod's solved span: what a garment fouls is the rod underneath, so the
    // usable length is measured to 1" above it whether the solver's own segment
    // boundary sits higher or lower than that.
    datum = (below.toUnits ?? 0) + clearance;
  } else if (!below && opts.hasBottomShelf === false) {
    // Exception 1 — nothing below and no bottom shelf: 1" off the FLOOR.
    datum = clearance;
  }
  return Math.max(0, top - datum);
}

/**
 * Where a SPLIT lands when the designer states no height: the MIDPOINT of the
 * space (ADR 0065 part 2), which is the same "unstated falls to the middle"
 * convention the door split already uses (ADR 0060 item 3). Returns null for a
 * space with no real span, which cannot be split at all.
 */
export function spaceSplitDefaultUnits(space) {
  if (!space || !(space.toUnits - space.fromUnits > EPS)) return null;
  return (space.fromUnits + space.toUnits) / 2;
}

/**
 * The N evenly spaced shelf TOP SURFACES a count implies across one span — the
 * ONE even-spacing mechanism (ADR 0060 item 9), lifted out of `decorate` so the
 * opening-insert (ADR 0061 item D) reuses it instead of growing a second one.
 *
 * The convention is the shelf-group / shoe-shelf convention verbatim: pitch =
 * span / count, one shelf at the bottom of each pitch slot.
 */
export function evenShelfLines(fromUnits, toUnits, count) {
  const span = toUnits - fromUnits;
  if (!Number.isInteger(count) || count <= 0 || !(span > EPS)) return [];
  const pitch = span / count;
  return Array.from({ length: count }, (_, k) => fromUnits + pitch * k);
}

/** Per-kind solved extras — what the 4b renderer will actually draw. */
function decorate(solved, stack = drawerStackOpts(), shelves = {}) {
  if (solved.kind === 'rod') {
    // The physical rod hangs at the TOP of its zone; the zone is the hang.
    solved.rodAtUnits = solved.toUnits;
  }
  if (solved.kind === 'shelf') {
    // The real board thickness, so a DOUBLE-THICK shelf draws as ONE thick
    // board rather than the renderer re-deriving it from a rule it cannot see.
    solved.thicknessUnits = solved.toUnits - solved.fromUnits;
  }
  if (solved.kind === 'shoeShelves' || solved.kind === 'shelfGroup') {
    // ONE evenly-spaced-lines mechanism (ADR 0060 item 9): a shelf group
    // pitches exactly the way shoe shelves always have.
    const span = solved.toUnits - solved.fromUnits;
    solved.pitchUnits = solved.count > 0 ? span / solved.count : 0;
  }
  if (solved.kind === 'shelfGroup') {
    // The group's shelf lines, SOLVED once here rather than re-derived in the
    // renderer: they are the positions that snap to system holes (ADR 0061),
    // and a drawing that re-computed them would draw a different composition
    // from the one the model believes in.
    const t = shelfThicknessUnits(solved, shelves.shelfT ?? 0.75, shelves.mult ?? DOUBLE_THICK_MULTIPLIER);
    solved.thicknessUnits = t;
    const tops = evenShelfLines(solved.fromUnits, solved.toUnits, solved.count);
    if (shelves.snap && shelves.rows?.elevations?.length && tops.length) {
      // Snap on each shelf's HOLE REFERENCE (bottom face, or centre when
      // fixed), then read the tops back off the holes — the same two rules
      // every other shelf obeys.
      const refs = tops.map((top) => shelfHoleReference(
        { fromUnits: top - t, toUnits: top }, { fixed: solved.fixed === true },
      ));
      const holes = snapShelfRowToHoles(refs, shelves.rows);
      solved.holeElevations = holes.slice();
      solved.shelfLines = holes.map((h, i) => (
        Number.isFinite(h) ? h + (solved.fixed === true ? t / 2 : t) : tops[i]
      ));
    } else {
      solved.shelfLines = tops;
    }
  }
  if (solved.kind === 'drawerBank') {
    // The bank's OCCUPIED height is still the SSH drawer tool's formula
    // verbatim (ADR 0060 item 10) — that is what the band gives up to it.
    solved.stackHeightUnits = stackHeight(solved.drawers, stack);
    // WHERE THE FACES LAND — BOTH ENDS OVERLAY THEIR BOARD (owner, 2026-08-09):
    // "the bottom drawer … should be 1/4" [resolved same day to 3/8"] overlay
    // of the fixed shelf (normally the bottom shelf) of a drawer stack", and
    // earlier "since there is 3/8" overlay of the drawer faces with the top
    // fixed shelf, the 48" should be compared to the top of the drawer face
    // (which should be the same as the middle of the fixed shelf)". One rule,
    // `drawer_overlay_units`, read at both ends — never the literal 3/8".
    //
    // So the stack is laid BOTTOM-UP off the board it stands on: the bottom
    // face laps `overlay` DOWN over that shelf exactly as the top face laps
    // `overlay` UP over the one above. Because the overlay is half a board and
    // a FIXED shelf is centred on its system hole (ADR 0061), each face edge
    // lands on the shelf's mid-thickness — a 32 mm hole centre.
    //
    // THE GAP BETWEEN FACES IS THE RULE, NOT A REMAINDER (owner, 2026-08-09:
    // 3/8" overlay "is what produces the 1/8" gaps the shop expects … between
    // drawers on side-by-side sections, and vertically between drawer faces").
    // So the faces are never stretched or spread to fill a box: the stack has
    // exactly ONE height, and the bank's BOARDS are what move to meet it — see
    // `isBankBoard` in deriveShelfPins. A bank that is nonetheless given more
    // box than it needs (a designer's own pinned shelf above it, say) keeps its
    // bottom seating, the owner's stated datum, and shows the surplus under the
    // board above rather than under the bottom face.
    const gap = stack.spacing;
    solved.drawerGapUnits = gap;
    // The two anchors the stack lives between — the boards' mid-thicknesses.
    const bottomAnchor = solved.fromUnits - stack.overlay;
    const topAnchor = solved.toUnits + stack.overlay;
    const built = new Array(solved.drawers.length);
    let cursor = bottomAnchor; // the bottom face's UNDERSIDE
    for (let i = solved.drawers.length - 1; i >= 0; i -= 1) {
      const d = solved.drawers[i];
      const height = isPos(d?.heightUnits) ? d.heightUnits : 0;
      // An OVERFLOWING bank is clamped, never drawn out of its box: the stack
      // seats at the bottom, so what does not fit collapses at the TOP.
      const from = Math.min(cursor, topAnchor);
      built[i] = { ...d, fromUnits: from, toUnits: Math.min(cursor + height, topAnchor) };
      cursor += height + gap;
    }
    solved.drawers = built;
    // The elevation the 48" maximum is judged at: the TOP OF THE TOP DRAWER
    // FACE, off the floor. It is a REACH limit — "the drawer tool… warns if
    // the stack is over 48" tall including base trim" — so it is measured from
    // the floor the designer stands on, not from the bank's own base: a bank
    // composed high in a tall unit is over the limit however short it is.
    solved.faceTopUnits = solved.drawers[0]?.toUnits ?? null;
    // …and the same figure as a HEIGHT above the board the bank stands on. It
    // no longer contains the toe (ADR 0063 — the toe holds the DECK off the
    // floor, below the band), so for a floor-standing bank the reach off the
    // floor is this plus the toe and that deck, which is exactly where the
    // band bottom of a real placed unit already is.
    solved.stackToFaceTopUnits = Number.isFinite(solved.faceTopUnits)
      ? solved.faceTopUnits - solved.fromUnits : 0;
    solved.stackOverMax = Number.isFinite(solved.faceTopUnits)
      && solved.faceTopUnits > stack.maxHeight + EPS;
  }
}
